import bem from "easy-bem";
import { useNotifications } from "features/notifications/useNotifications";
import { useFlag } from "hooks/useFlags";
import { useUser } from "hooks/useUser";
import React from "react";
import CountBadge from "ui/CountBadge";

import "./ViewSwitch.css";

const cn = bem("view-switch");

type ViewType =
  | "mine"
  | "drafts"
  | "favorites"
  | "reviews"
  | "moderations"
  | "deletions"
  | "comments"
  | "activity"
  | "notifications";

type ViewSwitchProps = {
  value: ViewType;
  onChange: (value: ViewType) => void;
  isModeratorial?: boolean;
  isMyContent?: boolean;
};

const ViewSwitch: React.FC<ViewSwitchProps> = ({
  value,
  onChange,
  isModeratorial = false,
  isMyContent = false,
}) => {
  const commentsEnabled = useFlag("commentsEnabled");
  const notificationsEnabled = useFlag("notificationsTest");
  const { isModerator } = useUser();

  // channels.comments — отдельный, самостоятельный канал (не входит в бейдж
  // колокольчика, см. notifications/utils.py BELL_CHANNELS); channels.activity —
  // источник бейджа вкладки "Уведомления" ниже.
  const { channels: notificationChannels } = useNotifications();
  const commentsNotificationCount = notificationChannels.comments?.count ?? 0;
  const activityNotificationCount = notificationChannels.activity?.count ?? 0;

  // Журнал активности: в своём профиле всегда, в чужом — только модератору
  // ("пользователь видит своё, модератор чужое")
  const showActivityTab = isMyContent || isModerator;

  // Уведомления — отдельная от журнала активности вкладка, только свой профиль
  const showNotificationsTab =
    isMyContent && !isModeratorial && notificationsEnabled;

  return (
    <div className={cn()}>
      {!isModeratorial && (
        <>
          <button
            className={cn("button", { active: value === "mine" })}
            onClick={() => onChange("mine")}
          >
            {isMyContent ? "Мои публикации" : "Публикации"}
          </button>

          {isMyContent && (
            <button
              className={cn("button", { active: value === "drafts" })}
              onClick={() => onChange("drafts")}
            >
              Мои черновики
            </button>
          )}

          {isMyContent && (
            <button
              className={cn("button", { active: value === "favorites" })}
              onClick={() => onChange("favorites")}
            >
              Избранное
            </button>
          )}
        </>
      )}

      {/* <button
        className={cn("button", { active: value === "reviews" })}
        onClick={() => onChange("reviews")}
      >
        Ревью
      </button> */}

      {commentsEnabled && (
        <button
          className={cn("button", { active: value === "comments" })}
          onClick={() => onChange("comments")}
        >
          Комментарии
          {isMyContent && <CountBadge count={commentsNotificationCount} />}
        </button>
      )}

      {(isMyContent || isModeratorial) && (
        <button
          className={cn("button", { active: value === "moderations" })}
          onClick={() => onChange("moderations")}
        >
          Скрытое модератором
        </button>
      )}

      {!isModeratorial && showActivityTab && (
        <button
          className={cn("button", { active: value === "activity" })}
          onClick={() => onChange("activity")}
        >
          Журнал активности
        </button>
      )}

      {showNotificationsTab && (
        <button
          className={cn("button", { active: value === "notifications" })}
          onClick={() => onChange("notifications")}
        >
          Уведомления
          <CountBadge count={activityNotificationCount} />
        </button>
      )}

      {isModeratorial && (
        <button
          className={cn("button", { active: value === "deletions" })}
          onClick={() => onChange("deletions")}
        >
          Удалённые
        </button>
      )}
    </div>
  );
};

export default ViewSwitch;
